import * as layout from '../../../components/markLayout.module.css';
import Table from '@hero/ui-kit/tables/Table';
import type { TableColumn } from '@hero/ui-kit/tables/Table/TableColHeader';
import { MarkInput, MarkLabel } from '../../../components/MarkControls';
import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import useDripStats from '../../../api/useDripStats';
import { EXIT_REASON_LABELS } from '../../../constants';
import type { DripDetail, DripStepStats, DripExitReason } from '../../../types';
type Props = {
    drip: DripDetail;
};
const pct = (n: number | null): string => (n != null ? `${(n * 100).toFixed(1)}%` : '—');
const Tile: React.FC<{
    label: string;
    value: number;
    footnote?: string;
}> = ({ label, value, footnote }) => (
    <div
        style={{
            textAlign: 'center',
            minWidth: '10rem'
        }}
        className={layout.panel}
    >
        <P
            noDefaultMargin
            strong
            styles={{
                fontSize: '2rem'
            }}
        >
            {value.toLocaleString()}
        </P>
        <P
            noDefaultMargin
            size="small"
            styles={{
                color: 'var(--mark-muted)'
            }}
        >
            {label}
        </P>
        {footnote && (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginTop: '0.2rem'
                }}
            >
                {footnote}
            </P>
        )}
    </div>
);
const stepColumns: TableColumn<DripStepStats>[] = [
    {
        colKey: 'Step',
        name: 'Step',
        render: (s) => {
            return <>{s.step_id}</>;
        }
    },
    {
        colKey: 'Type',
        name: 'Type',
        render: (s) => {
            return <>{s.type}</>;
        }
    },
    {
        colKey: 'Waiting',
        name: 'Waiting',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return <>{isActivity && s.waiting != null ? s.waiting.toLocaleString() : '—'}</>;
        }
    },
    {
        colKey: 'Sent',
        name: 'Sent',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return <>{isActivity && s.sent != null ? s.sent.toLocaleString() : '—'}</>;
        }
    },
    {
        colKey: 'Unsubscribed',
        name: 'Unsubscribed',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return (
                <>{isActivity && s.unsubscribed != null ? s.unsubscribed.toLocaleString() : '—'}</>
            );
        }
    },
    {
        colKey: 'Open',
        name: 'Open',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return <>{isActivity ? pct(s.open_rate) : '—'}</>;
        }
    },
    {
        colKey: 'Click',
        name: 'Click',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return <>{isActivity ? pct(s.click_rate) : '—'}</>;
        }
    },
    {
        colKey: 'Unsub',
        name: 'Unsub',
        align: 'right',
        headAlign: 'right',
        render: (s) => {
            const isActivity = s.type === 'activity';
            return <>{isActivity ? pct(s.unsubscribe_rate) : '—'}</>;
        }
    }
];
const pad = (n: number): string => String(n).padStart(2, '0');
// <input type="date"> values are local calendar days.
const toLocalDateInput = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const defaultSince = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDateInput(d);
};
const defaultUntil = (): string => toLocalDateInput(new Date());
// The BE reads a bare date as midnight UTC and filters timestamp <= until,
// so send explicit local day bounds as UTC instants instead.
const localDayBoundIso = (value: string, end: boolean): string | undefined => {
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return undefined;
    const date = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
    return date.toISOString();
};
const Stats: React.FC<Props> = ({ drip }) => {
    const [since, setSince] = useState(defaultSince);
    const [until, setUntil] = useState(defaultUntil);
    const {
        data: statsResponse,
        isLoading,
        error
    } = useDripStats(drip.drip_id, {
        since: localDayBoundIso(since, false),
        until: localDayBoundIso(until, true)
    });
    const stats = statsResponse?.data;
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    gap: '0.8rem',
                    alignItems: 'center',
                    marginBottom: '1.2rem'
                }}
            >
                <MarkLabel>
                    Since{' '}
                    <MarkInput
                        type="date"
                        value={since}
                        onChange={(e) => setSince(e.target.value)}
                    />
                </MarkLabel>
                <MarkLabel>
                    Until{' '}
                    <MarkInput
                        type="date"
                        value={until}
                        onChange={(e) => setUntil(e.target.value)}
                    />
                </MarkLabel>
            </div>
            {isLoading ? (
                <InlineLoader />
            ) : error ? (
                <QueryInlineError error={error} />
            ) : !stats ? (
                <P>No stats available.</P>
            ) : (
                <>
                    <div
                        style={{
                            display: 'flex',
                            gap: '0.8rem',
                            flexWrap: 'wrap',
                            marginBottom: '1.6rem'
                        }}
                    >
                        <Tile label="Enrolled" value={stats.funnel.enrolled} footnote="all-time" />
                        <Tile label="Active" value={stats.funnel.active} footnote="all-time" />
                        <Tile
                            label="Completed"
                            value={stats.funnel.completed}
                            footnote="all-time"
                        />
                        <Tile label="Exited" value={stats.funnel.exited} footnote="all-time" />
                    </div>
                    <div
                        style={{
                            marginBottom: '1.6rem'
                        }}
                        className={layout.tableScroll}
                    >
                        <P
                            strong
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.6rem'
                            }}
                        >
                            Exit reasons{' '}
                            <span
                                style={{
                                    fontWeight: 400,
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                (all-time)
                            </span>
                        </P>
                        <Table
                            columns={[
                                { colKey: 'reason', name: 'Reason' },
                                {
                                    colKey: 'count',
                                    name: 'Exited',
                                    align: 'right',
                                    headAlign: 'right',
                                    render: (row) => row.count.toLocaleString()
                                }
                            ]}
                            data={(Object.keys(stats.exit_reasons) as DripExitReason[])
                                .filter((k) => stats.exit_reasons[k] > 0)
                                .map((k) => ({
                                    reason: EXIT_REASON_LABELS[k] ?? k,
                                    count: stats.exit_reasons[k]
                                }))}
                            rowKey="reason"
                        />
                    </div>
                    {stats.by_step.length > 0 && (
                        <div
                            style={{
                                marginBottom: '1.6rem',
                                overflowX: 'auto'
                            }}
                            className={layout.tableScroll}
                        >
                            <P
                                strong
                                noDefaultMargin
                                styles={{
                                    marginBottom: '0.6rem'
                                }}
                            >
                                By step
                            </P>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)',
                                    marginBottom: '0.6rem'
                                }}
                            >
                                Since/until scope: sent, rates, and unsubscribed only. Waiting is
                                all-time.
                            </P>
                            <Table columns={stepColumns} data={stats.by_step} rowKey="step_id" />
                        </div>
                    )}
                    {stats.by_variant.length > 0 && (
                        <div
                            style={{
                                overflowX: 'auto'
                            }}
                            className={layout.tableScroll}
                        >
                            <P
                                strong
                                noDefaultMargin
                                styles={{
                                    marginBottom: '0.6rem'
                                }}
                            >
                                By variant
                            </P>
                            <Table
                                columns={[
                                    {
                                        colKey: 'Variant',
                                        name: 'Variant',
                                        render: (v) => {
                                            return <>{v.variant}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Enrolled',
                                        name: 'Enrolled',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{v.enrolled.toLocaleString()}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Completed',
                                        name: 'Completed',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{v.completed.toLocaleString()}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Unsubscribed',
                                        name: 'Unsubscribed',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{v.unsubscribed.toLocaleString()}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Open',
                                        name: 'Open',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{pct(v.open_rate)}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Click',
                                        name: 'Click',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{pct(v.click_rate)}</>;
                                        }
                                    },
                                    {
                                        colKey: 'Unsub',
                                        name: 'Unsub',
                                        align: 'right',
                                        headAlign: 'right',
                                        render: (v) => {
                                            return <>{pct(v.unsubscribe_rate)}</>;
                                        }
                                    }
                                ]}
                                data={stats.by_variant}
                                rowKey="variant"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
export default Stats;
